import { useId } from 'react';
import { formatCurrency, formatMonth } from '../domain/format';

export interface ChartSeries {
  name: string;
  values: Array<number | null>;
  tone: 'primary' | 'baseline' | 'positive' | 'warning';
  dashed?: boolean;
}

interface TrendChartProps {
  labels: string[];
  series: ChartSeries[];
  ariaLabel: string;
  height?: number;
  zeroLine?: boolean;
}

const toneColor: Record<ChartSeries['tone'], string> = {
  primary: 'var(--teal-500)',
  baseline: 'var(--ink-400)',
  positive: 'var(--green-500)',
  warning: 'var(--amber-500)',
};

export const makePath = (
  values: Array<number | null>,
  min: number,
  max: number,
  width: number,
  height: number,
  left: number,
  top: number,
) => {
  const range = max - min || 1;
  const innerWidth = width - left - 18;
  const innerHeight = height - top - 34;
  let drawing = false;
  return values
    .map((value, index) => {
      if (value === null) {
        drawing = false;
        return null;
      }
      const x = left + (values.length === 1 ? innerWidth / 2 : (index / (values.length - 1)) * innerWidth);
      const y = top + ((max - value) / range) * innerHeight;
      const command = drawing ? 'L' : 'M';
      drawing = true;
      return `${command} ${x.toFixed(2)} ${y.toFixed(2)}`;
    })
    .filter(Boolean)
    .join(' ');
};

export const TrendChart = ({
  labels,
  series,
  ariaLabel,
  height = 260,
  zeroLine = false,
}: TrendChartProps) => {
  const titleId = useId();
  const width = 920;
  const left = 74;
  const top = 16;
  const values = series.flatMap((item) => item.values).filter((value): value is number => value !== null);
  let min = Math.min(...values, 0);
  let max = Math.max(...values, 1);
  const padding = Math.max((max - min) * 0.12, 1);
  min -= padding;
  max += padding;
  const gridValues = Array.from({ length: 5 }, (_, index) => min + ((max - min) * index) / 4).reverse();
  const innerHeight = height - top - 34;
  const zeroY = top + ((max - 0) / (max - min || 1)) * innerHeight;
  const tickStep = Math.max(1, Math.ceil(labels.length / 6));

  return (
    <div className="chart-wrap">
      <svg
        className="trend-chart"
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-labelledby={titleId}
        preserveAspectRatio="xMidYMid meet"
      >
        <title id={titleId}>{ariaLabel}</title>
        {gridValues.map((value) => {
          const y = top + ((max - value) / (max - min || 1)) * innerHeight;
          return (
            <g key={value}>
              <line x1={left} x2={width - 18} y1={y} y2={y} className="chart-grid" />
              <text x={left - 12} y={y + 4} textAnchor="end" className="chart-label">
                {formatCurrency(value, { compact: true })}
              </text>
            </g>
          );
        })}
        {zeroLine && zeroY > top && zeroY < height - 34 ? (
          <line x1={left} x2={width - 18} y1={zeroY} y2={zeroY} className="chart-zero" />
        ) : null}
        {series.map((item) => (
          <path
            key={item.name}
            d={makePath(item.values, min, max, width, height, left, top)}
            fill="none"
            stroke={toneColor[item.tone]}
            strokeWidth="3"
            strokeDasharray={item.dashed ? '8 7' : undefined}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ))}
        {labels.map((label, index) => {
          if (index % tickStep !== 0 && index !== labels.length - 1) return null;
          const x = left + (labels.length === 1 ? 0 : (index / (labels.length - 1)) * (width - left - 18));
          return (
            <text key={label} x={x} y={height - 8} textAnchor="middle" className="chart-label">
              {formatMonth(label)}
            </text>
          );
        })}
      </svg>
      <div className="chart-legend" aria-hidden="true">
        {series.map((item) => (
          <span key={item.name}>
            <i
              className={item.dashed ? 'legend-line dashed' : 'legend-line'}
              style={{ backgroundColor: toneColor[item.tone] }}
            />
            {item.name}
          </span>
        ))}
      </div>
    </div>
  );
};
